import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import { Icon } from '../../../../atoms/Icon'
import type { EmploymentTrend } from '../../employmentService'

interface JobsPerEstablishmentChartProps {
  data: EmploymentTrend[]
  height?: number
  t: TFunction
}

export interface JobsPerEstablishmentChartRef {
  exportChart: () => string | null
}

export const JobsPerEstablishmentChart = forwardRef<
  JobsPerEstablishmentChartRef,
  JobsPerEstablishmentChartProps
>(({ data, height = 400, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Calculate jobs per establishment ratio
    const years = sortedData.map((d) => d.year.toString())
    const ratios = sortedData.map((d) => {
      return d.total_establishments > 0
        ? d.total_employment / d.total_establishments
        : 0
    })

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: function (params: any) {
          const year = params[0].axisValue
          const ratio = params[0].value
          const dataIndex = params[0].dataIndex
          const employment = sortedData[dataIndex].total_employment
          const establishments = sortedData[dataIndex].total_establishments

          return `
            <div style="text-align: left;">
              <strong>${t('charts.jobsPerEstablishment.tooltips.year')}: ${year}</strong><br/>
              <span style="color: #3b82f6;">● ${t('charts.jobsPerEstablishment.tooltips.ratio')}: ${ratio.toFixed(2)} ${t('charts.jobsPerEstablishment.tooltips.jobsPerEstablishment')}</span><br/>
              <span style="color: #6b7280;">● ${t('charts.jobsPerEstablishment.tooltips.employment')}: ${employment.toLocaleString()}</span><br/>
              <span style="color: #6b7280;">● ${t('charts.jobsPerEstablishment.tooltips.establishments')}: ${establishments.toLocaleString()}</span>
            </div>
          `
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: {
          fontSize: 11,
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: t('charts.jobsPerEstablishment.axis.ratio'),
        axisLabel: {
          fontSize: 11,
          formatter: '{value}',
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: t('charts.jobsPerEstablishment.axis.ratio'),
          type: 'line',
          data: ratios,
          lineStyle: {
            color: '#3b82f6',
            width: 3,
          },
          itemStyle: {
            color: '#3b82f6',
          },
          symbol: 'circle',
          symbolSize: 6,
          emphasis: {
            itemStyle: {
              borderColor: '#3b82f6',
              borderWidth: 2,
            },
          },
          markLine: {
            silent: true,
            lineStyle: {
              color: '#ef4444',
              type: 'dashed',
              width: 2,
            },
            data: [
              {
                type: 'average',
                name: t('charts.jobsPerEstablishment.average'),
                label: {
                  formatter: `${t('charts.jobsPerEstablishment.average')}: {c}`,
                },
              },
            ],
          },
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, t])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.jobsPerEstablishment.noData')}
      </div>
    )
  }

  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  const firstRatio =
    firstYear.total_establishments > 0
      ? firstYear.total_employment / firstYear.total_establishments
      : 0
  const lastRatio =
    lastYear.total_establishments > 0
      ? lastYear.total_employment / lastYear.total_establishments
      : 0

  const ratioChange =
    firstRatio > 0 ? ((lastRatio - firstRatio) / firstRatio) * 100 : 0

  const avgRatio =
    sortedData.reduce((sum, d) => {
      return (
        sum +
        (d.total_establishments > 0
          ? d.total_employment / d.total_establishments
          : 0)
      )
    }, 0) / sortedData.length

  return (
    <div>
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: `${height}px`,
          minHeight: '300px',
        }}
      />

      {/* Efficiency insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="trending-up" size="xs" />{' '}
          {t('charts.jobsPerEstablishment.summary.title', {
            startYear: firstYear.year,
            endYear: lastYear.year,
          })}
        </div>
        <div
          style={{
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.4',
          }}
        >
          •{' '}
          <strong>
            {t('charts.jobsPerEstablishment.summary.currentRatio')}:
          </strong>{' '}
          {lastRatio.toFixed(2)}{' '}
          {t('charts.jobsPerEstablishment.summary.jobsPerEstablishment')}
          <br />•{' '}
          <strong>
            {t('charts.jobsPerEstablishment.summary.historicalChange')}:
          </strong>{' '}
          {ratioChange >= 0 ? '+' : ''}
          {ratioChange.toFixed(1)}% ({firstRatio.toFixed(2)} →{' '}
          {lastRatio.toFixed(2)})<br />•{' '}
          <strong>
            {t('charts.jobsPerEstablishment.summary.periodAverage')}:
          </strong>{' '}
          {avgRatio.toFixed(2)}{' '}
          {t('charts.jobsPerEstablishment.summary.jobsPerEstablishment')}
          <br />•{' '}
          <strong>
            {t('charts.jobsPerEstablishment.summary.interpretation')}:
          </strong>{' '}
          {ratioChange > 10
            ? t('charts.jobsPerEstablishment.summary.consolidating')
            : ratioChange < -10
              ? t('charts.jobsPerEstablishment.summary.fragmenting')
              : lastRatio > 10
                ? t(
                    'charts.jobsPerEstablishment.summary.largeEmployerDominated'
                  )
                : lastRatio < 5
                  ? t(
                      'charts.jobsPerEstablishment.summary.smallBusinessEconomy'
                    )
                  : t('charts.jobsPerEstablishment.summary.balancedMix')}
        </div>
      </div>
    </div>
  )
})

JobsPerEstablishmentChart.displayName = 'JobsPerEstablishmentChart'
